import React, { Dispatch } from "react";
import { Callout, Intent } from "@blasterjs/core";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import ImageViewer from "../components/ImageViewer";
import InfoSidebarCase from "../components/InfoSidebarCase";
import TopBarCase from "../components/TopBarCase";
import Content from "../components/Content";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  caseFetch,
  resetCaseImageViewer,
  deficientFeatureOptionsFetch,
  imageQualityAssessmentFetch
} from "../slices/caseImageViewer";
import { studyFetchRequest } from "../slices/studyConfiguration";
import { queryDetailsFetchByCase, setCurrentViewQueryObjectType } from "../slices/queryDetails";
import { RolePermissions, userIsAdminOrReader } from "../permissions";
import { AdjudicationParadigm, QueryObjectType } from "../models";
import { resetMetadataImages } from "../slices/metadataImages";
import { studyFetch } from "../slices/studies";
import ImageSelector from "../components/image-viewer/ImageSelector";
import { ViewerProfiler } from "../profiling";

function resetCaseAll(dispatch: Dispatch<any>) {
  dispatch(resetCaseImageViewer());
  dispatch(resetMetadataImages());
}

const Case = () => {
  const params = useParams();
  const id = params.id || "no-id";
  const studyId = params.studyId || "no-study-id";

  const dispatch = useAppDispatch();

  const study = useAppSelector(state => state.studies.study);
  const histoCase = useAppSelector(state => state.caseImageViewer.histoCase);
  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);
  const config = useAppSelector(state => state.config.config);
  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);
  const queryDetailsResource = useAppSelector(state => state.queryDetails);
  const queryDetails =
    "resource" in queryDetailsResource.queryDetails
      ? queryDetailsResource.queryDetails.resource
      : [];

  const studyName = ("resource" in study && study.resource.study.name) || null;

  useEffect(() => {
    dispatch(caseFetch(id));
    dispatch(studyFetchRequest(studyId));
    dispatch(studyFetch(studyId));
    dispatch(queryDetailsFetchByCase(id));
    dispatch(
      setCurrentViewQueryObjectType({
        queryViewObjectType: QueryObjectType.Case,
        viewObjectId: id
      })
    );
    return () => {
      resetCaseAll(dispatch);
    };
  }, [id, studyId]);

  const isUserAdminOrReader = userIsAdminOrReader(
    ("resource" in loggedInUser && loggedInUser.resource) || null
  );

  const hasAiAnnotations =
    (histoCase && "resource" in histoCase && histoCase.resource.acceptAiAnnotations) || false;

  // Per-reader AI: whether the viewing reader currently accepts (holds their own copy of) AI
  // annotations on the open image. Sourced from the opened ImageWithAnnotations, which the backend
  // now derives from readerHasAiCopy (the old per-image images.accept_ai_annotations flag is gone).
  const readerAcceptsAi =
    selectedImage !== null && "resource" in selectedImage
      ? selectedImage.resource.acceptAiAnnotations
      : false;

  const userCanDeleteAnnotations = isUserAdminOrReader;

  // IQA form is only offered on Hybrid studies with the feature enabled (mirrors the backend gate).
  const iqaEnabled =
    "resource" in study &&
    study.resource.study.adjudicationParadigm === AdjudicationParadigm.Hybrid &&
    study.resource.study.enableImageQualityAssessment;

  const selectedImageId =
    selectedImage !== null && "resource" in selectedImage
      ? selectedImage.resource.imageAndQuery.image.id
      : null;

  // Load the deficient-feature options once the study is known to use IQA. The list is static (it
  // does not vary by case), but resetCaseImageViewer clears it on every case unmount, so guard on
  // it already being loaded to avoid a redundant refetch on each case navigation.
  const deficientFeatureOptionsLoaded = useAppSelector(
    state => state.caseImageViewer.deficientFeatureOptions.length > 0
  );
  useEffect(() => {
    if (iqaEnabled && !deficientFeatureOptionsLoaded) {
      dispatch(deficientFeatureOptionsFetch());
    }
  }, [iqaEnabled, deficientFeatureOptionsLoaded]);

  // Load the reader's saved IQA for the currently-selected image so the form pre-fills. The caseId
  // comes from the route (id), not histoCase, so this fires as soon as an image is selected — on
  // initial load the image resolves before histoCase, and gating on histoCase would drop this first
  // fetch and the form would never appear until the user switched images.
  useEffect(() => {
    if (iqaEnabled && selectedImageId !== null) {
      dispatch(imageQualityAssessmentFetch({ caseId: id, imageId: selectedImageId }));
    }
  }, [iqaEnabled, selectedImageId, id]);

  const errorText =
    "errorMessage" in config
      ? config.errorMessage
      : histoCase && "errorMessage" in histoCase
      ? histoCase.errorMessage
      : selectedImage && "errorMessage" in selectedImage
      ? selectedImage.errorMessage
      : null;

  const content =
    "resource" in config &&
    histoCase !== null &&
    "resource" in histoCase &&
    (selectedImage === null || "resource" in selectedImage) &&
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.AP_ImageViewer_ViewimageViewer]) ? (
      <ViewerProfiler id="ImageViewer">
        <ImageViewer
          imageWithAnnotations={selectedImage === null ? selectedImage : selectedImage.resource}
          tileServerLocation={config.resource.tileServerLocation}
          topBar={
            <TopBarCase
              histoCase={histoCase.resource.caseWithStatus}
              isCR={loggedInUser.resource.isCR()}
            />
          }
          sidebar={
            "resource" in loggedInUser &&
            (loggedInUser.resource.can([RolePermissions.AP_ImageCaseViewer_ViewOverviewtab]) ||
              loggedInUser.resource.isUploader()) ? (
              <InfoSidebarCase
                studyName={studyName}
                histoCase={histoCase.resource}
                selectedImage={selectedImage === null ? null : selectedImage.resource.imageAndQuery}
                userCanDeleteAnnotations={userCanDeleteAnnotations}
                queryDetails={queryDetails}
                loggedInUser={loggedInUser.resource}
                hasAiAnnotations={hasAiAnnotations}
                readerAcceptsAi={readerAcceptsAi}
              />
            ) : null
          }
          imageSelector={
            "resource" in loggedInUser &&
            (loggedInUser.resource.can([RolePermissions.AP_ImageCaseViewer_ViewOverviewtab]) ||
              loggedInUser.resource.isUploader()) ? (
              <ImageSelector histoCase={histoCase.resource} />
            ) : null
          }
        />
      </ViewerProfiler>
    ) : errorText ? (
      <Callout intent={Intent.DANGER}>{errorText}</Callout>
    ) : null;
  return (
    <Content
      isLoading={
        (histoCase !== null && "isPending" in histoCase) ||
        "isPending" in config ||
        (selectedImage !== null && "isPending" in selectedImage)
      }
    >
      {content}
    </Content>
  );
};

export default Case;
